import { useEffect, useState } from 'react'
import { Expandable, Button } from 'components'
import Flex from 'components/priority/Flex/Flex'
import { CaseImagesSection } from '../CaseImages'
import { useRecoilValue } from 'recoil'
import { caseAtom, caseValuesAtom } from 'atoms'
import { caseImagesAtom } from '@/atoms/caseImageAtoms'
import { useSetToast } from 'hooks'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { useCaseImageVariants } from '@/hooks/caseImages'
import { useTranslation } from 'react-i18next'
import { deleteCaseImageV2, moveImageToFront } from '@/api/caseImages'
import { useCaseImageUpload } from '@/hooks/useCaseImageUpload'
import { useFillCaseImages } from '@/hooks/editor/useFillCaseImages'

export const PersonalImages = ({
  saveCase,
  case_id: propCaseId,
  activeTab,
  setActiveTab,
}) => {
  const { t } = useTranslation()
  const handleError = useErrorHandler('PersonalImages')
  const caseData = useRecoilValue(caseAtom)
  const caseIdFromValues = useRecoilValue(caseValuesAtom('id'))
  const case_id = propCaseId || caseData?.id || caseIdFromValues?.value

  const caseImages = useRecoilValue(caseImagesAtom)
  const { loadCaseImages } = useCaseImageVariants()
  const isAdmin = useIsAdmin()

  const [expandedPersonalImages, setExpandedPersonalImages] = useState(true)
  const [imagesLoading, setImagesLoading] = useState(false)
  const setToast = useSetToast()

  const fillCaseImages = useFillCaseImages()

  const { uploadMultipleImages } = useCaseImageUpload({
    caseId: case_id,
    isAdmin,
    componentName: 'PersonalImages',
  })

  const removeArrayInput = async (index) => {
    try {
      await deleteCaseImageV2(case_id, caseImages[index].originalUrl)
      fillCaseImages(await loadCaseImages(case_id))
    } catch (error) {
      handleError(
        error?.response?.data?.message || error.message,
        error,
        null,
        'removeArrayInput',
      )
    }
  }

  useEffect(() => {
    setExpandedPersonalImages(true)
  }, [activeTab])

  const makeDefault = async (index) => {
    if (index === 0) return // Already at front

    try {
      await moveImageToFront(case_id, index)
      fillCaseImages(await loadCaseImages(case_id))
    } catch (error) {
      handleError(
        error?.response?.data?.message || error.message,
        error,
        null,
        'makeDefault',
      )
    }
  }

  const uploadCaseImages = async (filesArray) => {
    setImagesLoading(true)
    try {
      await uploadMultipleImages(filesArray)
      fillCaseImages(await loadCaseImages(case_id))
    } catch (error) {
      setToast({
        text: t('toast.someImagesFailedToUpload', { error }),
      })
    } finally {
      setImagesLoading(false)
    }
  }

  const saveAndContinue = (expanded) => {
    switch (expanded) {
      case 'personalImages':
        setExpandedPersonalImages(false)
        setActiveTab(t('caseDetails.tabs.memorialContributions.title'))
        break
      default:
        setExpandedPersonalImages(false)
    }
    saveCase()
  }

  return (
    <Flex column full padding="0 48px 0 0">
      <Flex column align="flex-start">
        <Expandable
          margin="0 0 32px 0"
          title={t(
            'caseDetails.tabs.imagesAndFamilyMemorials.imagesOfDeceased',
          )}
          show={[expandedPersonalImages, setExpandedPersonalImages]}
          full
        >
          <Flex full column>
            <CaseImagesSection
              caseId={case_id}
              presentation="takeover"
              dropzoneText={t(
                'caseDetails.tabs.imagesAndFamilyMemorials.dragAndDropFileHereToEdit',
              )}
              imagesLoading={imagesLoading}
              onDrop={(files) => uploadCaseImages(files)}
              onSetDefault={makeDefault}
              onDelete={removeArrayInput}
              onError={(message, error) =>
                handleError(message, error, null, 'CaseImagesSection')
              }
            />
          </Flex>
          <Flex justify="flex-end">
            <Button onClick={() => saveAndContinue('personalImages')} primary>
              {t(
                'caseDetails.tabs.imagesAndFamilyMemorials.saveAndContinueButton',
              )}
            </Button>
          </Flex>
        </Expandable>
      </Flex>
    </Flex>
  )
}

export default PersonalImages
